import apiClient, { API_ENDPOINTS } from './config';
import { filenameFromContentDisposition } from '../utils/download';
import {
  AdoWorkItemsResponse,
  DocumentClassification,
  PushRequirementTestCasesPayload,
  PushRequirementTestCasesResponse,
  RequirementAdoPushJob,
  RequirementAdoPushJobStartResponse,
  RequirementAdoCompareSyncJob,
  RequirementAdoCompareSyncJobStartResponse,
  AdoCompareResponse,
  AdoCompareBulkSyncResponse,
  AdoCompareSyncResponse,
  QueueRequirementGenerationPayload,
  RequirementGenerationPreflightPayload,
  RequirementGenerationPreflightResponse,
  RequirementDocument,
  RequirementGenerationJob,
  RequirementTestDetailResponse,
  RequirementTestWorkspaceResponse,
  AdoItemNote,
  ReviewRequirementsPayload,
  ReviewRequirementsResponse,
  TestCaseVersion,
  UpdateTestCasePayload,
} from '../types/requirements';

export const listRequirementDocuments = async (
  projectId: number | string,
): Promise<RequirementDocument[]> => {
  const response = await apiClient.get<RequirementDocument[]>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_DOCUMENTS(projectId),
  );
  return response.data || [];
};

export const uploadRequirementDocuments = async (
  projectId: number | string,
  files: File[],
  options?: { notes?: string; classification?: DocumentClassification },
): Promise<RequirementDocument[]> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  if (options?.notes) {
    formData.append('notes', options.notes);
  }
  if (options?.classification) {
    formData.append('classification', options.classification);
  }

  const response = await apiClient.post<RequirementDocument[]>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_DOCUMENTS(projectId),
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data || [];
};

/**
 * Upload a single requirement document with its own notes/context description.
 * Use this instead of uploadRequirementDocuments when each file needs individual metadata.
 */
export const uploadSingleRequirementDocument = async (
  projectId: number | string,
  file: File,
  options?: { notes?: string; classification?: DocumentClassification },
): Promise<RequirementDocument | null> => {
  const formData = new FormData();
  formData.append('files', file);
  if (options?.notes) formData.append('notes', options.notes);
  if (options?.classification) formData.append('classification', options.classification);
  const response = await apiClient.post<RequirementDocument[]>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_DOCUMENTS(projectId),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return (response.data || [])[0] ?? null;
};

export const deleteRequirementDocument = async (
  projectId: number | string,
  documentId: number | string,
): Promise<void> => {
  await apiClient.delete(
    API_ENDPOINTS.PROJECT_REQUIREMENT_DOCUMENT(projectId, documentId),
  );
};

export const reprocessRequirementDocument = async (
  projectId: number | string,
  documentId: number | string,
): Promise<RequirementDocument> => {
  const response = await apiClient.post<RequirementDocument>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_DOCUMENT_REPROCESS(projectId, documentId),
  );
  return response.data;
};

export const downloadRequirementDocument = async (
  projectId: number | string,
  documentId: number | string,
): Promise<Blob> => {
  const response = await apiClient.get(
    API_ENDPOINTS.PROJECT_REQUIREMENT_DOCUMENT_DOWNLOAD(projectId, documentId),
    { responseType: 'blob' },
  );
  return response.data;
};

export const queueRequirementGeneration = async (
  projectId: number | string,
  payload: QueueRequirementGenerationPayload,
): Promise<RequirementGenerationJob> => {
  const response = await apiClient.post<RequirementGenerationJob>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATIONS(projectId),
    payload,
  );
  return response.data;
};

export const preflightRequirementGeneration = async (
  projectId: number | string,
  payload: RequirementGenerationPreflightPayload,
): Promise<RequirementGenerationPreflightResponse> => {
  const response = await apiClient.post<RequirementGenerationPreflightResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATION_PREFLIGHT(projectId),
    payload,
  );
  return response.data;
};

export const listRequirementGenerations = async (
  projectId: number | string,
): Promise<RequirementGenerationJob[]> => {
  const response = await apiClient.get<RequirementGenerationJob[]>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATIONS(projectId),
  );
  return response.data || [];
};

export const fetchRequirementGeneration = async (
  projectId: number | string,
  generationId: number | string,
): Promise<RequirementGenerationJob> => {
  const response = await apiClient.get<RequirementGenerationJob>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATION(projectId, generationId),
  );
  return response.data;
};

export interface DownloadedArtifactFile {
  blob: Blob;
  filename: string;
}

export const downloadRequirementArtifact = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  fallbackFilename = 'project-artifact.json',
): Promise<DownloadedArtifactFile> => {
  const response = await apiClient.get(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATION_ARTIFACT_DOWNLOAD(projectId, generationId, artifactId),
    { responseType: 'blob' },
  );
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(
      response.headers?.['content-disposition'],
      fallbackFilename,
    ),
  };
};

export const fetchRequirementTestWorkspace = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
): Promise<RequirementTestWorkspaceResponse> => {
  const response = await apiClient.get<RequirementTestWorkspaceResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE(projectId, generationId, artifactId),
  );
  return response.data;
};

export const fetchRequirementLiveTestWorkspace = async (
  projectId: number | string,
  generationId: number | string,
): Promise<RequirementTestWorkspaceResponse> => {
  const response = await apiClient.get<RequirementTestWorkspaceResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_LIVE_TEST_WORKSPACE(projectId, generationId),
  );
  return response.data;
};

export const pushRequirementTestCasesToAdo = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  payload: PushRequirementTestCasesPayload,
): Promise<PushRequirementTestCasesResponse> => {
  const response = await apiClient.post<PushRequirementTestCasesResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH(projectId, generationId, artifactId),
    payload,
  );
  return response.data;
};

export const startRequirementTestCasesAdoPush = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  payload: PushRequirementTestCasesPayload,
): Promise<RequirementAdoPushJobStartResponse> => {
  const response = await apiClient.post<RequirementAdoPushJobStartResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH_START(projectId, generationId, artifactId),
    payload,
  );
  return response.data;
};

export const fetchRequirementAdoPushJob = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  pushJobId: number | string,
): Promise<RequirementAdoPushJob> => {
  const response = await apiClient.get<RequirementAdoPushJob>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH_JOB(projectId, generationId, artifactId, pushJobId),
  );
  return response.data;
};

export const fetchLatestRequirementAdoPushJob = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
): Promise<RequirementAdoPushJob | null> => {
  const response = await apiClient.get<RequirementAdoPushJob | null>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH_LATEST(projectId, generationId, artifactId),
  );
  return response.data;
};

export const compareRequirementTestCasesWithAdo = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  payload: PushRequirementTestCasesPayload = {},
): Promise<AdoCompareResponse> => {
  const response = await apiClient.post<AdoCompareResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE(projectId, generationId, artifactId),
    payload,
  );
  return response.data;
};

export const syncAdoCompareChange = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  changeId: string,
): Promise<AdoCompareSyncResponse> => {
  const response = await apiClient.post<AdoCompareSyncResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC(projectId, generationId, artifactId),
    { change_id: changeId },
  );
  return response.data;
};

export const syncAdoCompareChanges = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  changeIds: string[],
): Promise<AdoCompareBulkSyncResponse> => {
  const response = await apiClient.post<AdoCompareBulkSyncResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_BULK(projectId, generationId, artifactId),
    { change_ids: changeIds },
  );
  return response.data;
};

export const startAdoCompareChangesSync = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  changeIds: string[],
  scope: 'row' | 'group' | 'all' | string = 'all',
): Promise<RequirementAdoCompareSyncJobStartResponse> => {
  const response = await apiClient.post<RequirementAdoCompareSyncJobStartResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_START(projectId, generationId, artifactId),
    { change_ids: changeIds, scope },
  );
  return response.data;
};

export const fetchAdoCompareSyncJob = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  syncJobId: number | string,
): Promise<RequirementAdoCompareSyncJob> => {
  const response = await apiClient.get<RequirementAdoCompareSyncJob>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_JOB(projectId, generationId, artifactId, syncJobId),
  );
  return response.data;
};

export const fetchLatestAdoCompareSyncJob = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
): Promise<RequirementAdoCompareSyncJob | null> => {
  const response = await apiClient.get<RequirementAdoCompareSyncJob | null>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_LATEST(projectId, generationId, artifactId),
  );
  return response.data;
};

export const fetchRequirementTestDetail = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  testKey: string,
): Promise<RequirementTestDetailResponse> => {
  const response = await apiClient.get<RequirementTestDetailResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_TEST(projectId, generationId, artifactId, testKey),
  );
  return response.data;
};

export const updateRequirementTestFolder = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  testKey: string,
  folderPath: string[],
): Promise<{ test_key: string; folder_path: string[]; folder_id: string; manual_folder_override: boolean }> => {
  const response = await apiClient.put(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_TEST_FOLDER(projectId, generationId, artifactId, testKey),
    { folder_path: folderPath },
  );
  return response.data;
};

export interface RenameRequirementTestFolderResponse {
  source_folder_path: string[];
  folder_path: string[];
  folder_id: string;
  renamed_test_count: number;
}

export const renameRequirementTestFolder = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  sourceFolderPath: string[],
  newName: string,
): Promise<RenameRequirementTestFolderResponse> => {
  const response = await apiClient.put<RenameRequirementTestFolderResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_FOLDER_RENAME(projectId, generationId, artifactId),
    { source_folder_path: sourceFolderPath, new_name: newName },
  );
  return response.data;
};

export const fetchAdoWorkItems = async (
  projectId: number | string,
): Promise<AdoWorkItemsResponse> => {
  const response = await apiClient.get<AdoWorkItemsResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_ADO_WORK_ITEMS(projectId),
  );
  return response.data;
};

export const reviewRequirements = async (
  projectId: number | string,
  payload: ReviewRequirementsPayload,
): Promise<ReviewRequirementsResponse> => {
  const response = await apiClient.post<ReviewRequirementsResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_REVIEW(projectId),
    payload,
  );
  return response.data;
};

export const postAdoNotes = async (
  projectId: number | string,
  notes: AdoItemNote[],
): Promise<{ results: { work_item_id: number; status: string; detail?: string }[] }> => {
  const response = await apiClient.post(
    API_ENDPOINTS.PROJECT_REQUIREMENT_ADO_NOTES(projectId),
    { notes },
  );
  return response.data;
};

// Feature 7: fetch version history for a test case
export const fetchTestCaseVersions = async (
  projectId: number | string,
  tcKey: string,
): Promise<TestCaseVersion[]> => {
  const response = await apiClient.get<TestCaseVersion[]>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_CASE_VERSIONS(projectId, tcKey),
  );
  return response.data || [];
};

// Feature 7: delete a test case
export const deleteRequirementTestCase = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  testKey: string,
): Promise<void> => {
  await apiClient.delete(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_TEST(projectId, generationId, artifactId, testKey),
  );
};

export interface DeleteRequirementTestFolderResponse {
  source_folder_path: string[];
  folder_id: string;
  deleted_test_count: number;
  deleted_test_keys: string[];
}

export const deleteRequirementTestFolder = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  sourceFolderPath: string[],
): Promise<DeleteRequirementTestFolderResponse> => {
  const response = await apiClient.delete<DeleteRequirementTestFolderResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_FOLDERS(projectId, generationId, artifactId),
    { data: { source_folder_path: sourceFolderPath } },
  );
  return response.data;
};

// Feature 7: inline-edit a test case
export const updateTestCase = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  testKey: string,
  payload: UpdateTestCasePayload,
): Promise<Record<string, any>> => {
  const response = await apiClient.patch<Record<string, any>>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_TEST(projectId, generationId, artifactId, testKey),
    payload,
  );
  return response.data;
};

// Feature 7: manually add a new test case to an artifact
export interface AddManualTestCasePayload {
  title: string;
  description?: string;
  steps?: Array<{ action: string; expected_result: string }>;
  priority?: string;
  folder_path?: string[];
}

export const addManualTestCase = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  payload: AddManualTestCasePayload,
): Promise<Record<string, any>> => {
  const response = await apiClient.post<Record<string, any>>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_TEST_WORKSPACE_TESTS(projectId, generationId, artifactId),
    payload,
  );
  return response.data;
};

// Gherkin generation

export interface GherkinContextFileEntry {
  filename: string;
  description: string;
}

export interface GherkinGenerateResponse {
  gherkin_job_id: number;
  status: string;
  source_artifact_id: number;
  created_at: string;
  context_files?: GherkinContextFileEntry[];
}

export interface GherkinStatusResponse {
  status: string;
  progress: number;
  gherkin_job_id: number | null;
  gherkin_artifact_id: number | null;
  download_url: string | null;
  error_message: string | null;
  created_at: string | null;
  completed_at: string | null;
  context_files?: GherkinContextFileEntry[];
}

export interface GherkinRunEntry {
  gherkin_job_id: number;
  status: string;
  progress: number;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  gherkin_artifact_id: number | null;
  context_files: GherkinContextFileEntry[];
}

export const generateGherkin = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
  contextFiles?: File[],
  contextDescriptions?: string[],
): Promise<GherkinGenerateResponse> => {
  if (contextFiles && contextFiles.length > 0) {
    const formData = new FormData();
    contextFiles.forEach((f) => formData.append('context_files', f));
    (contextDescriptions ?? []).forEach((d) => formData.append('context_descriptions', d));
    const response = await apiClient.post<GherkinGenerateResponse>(
      API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATE_GHERKIN(projectId, generationId, artifactId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }
  const response = await apiClient.post<GherkinGenerateResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GENERATE_GHERKIN(projectId, generationId, artifactId),
  );
  return response.data;
};

export const fetchGherkinStatus = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
): Promise<GherkinStatusResponse> => {
  const response = await apiClient.get<GherkinStatusResponse>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GHERKIN_STATUS(projectId, generationId, artifactId),
  );
  return response.data;
};

export const fetchGherkinRuns = async (
  projectId: number | string,
  generationId: number | string,
  artifactId: number | string,
): Promise<GherkinRunEntry[]> => {
  const response = await apiClient.get<GherkinRunEntry[]>(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GHERKIN_RUNS(projectId, generationId, artifactId),
  );
  return response.data;
};

export const downloadGherkinArtifact = async (
  projectId: number | string,
  gherkinJobId: number | string,
  artifactId: number | string,
  fallbackFilename = 'project-test-cases.feature',
): Promise<DownloadedArtifactFile> => {
  const response = await apiClient.get(
    API_ENDPOINTS.PROJECT_REQUIREMENT_GHERKIN_DOWNLOAD(projectId, gherkinJobId, artifactId),
    { responseType: 'blob' },
  );
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(
      response.headers?.['content-disposition'],
      fallbackFilename,
    ),
  };
};
